import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faHeart as faEmptyHeart } from '@fortawesome/free-regular-svg-icons';
import { faHeart as faFullHeart, faHeartBroken as faHalfHeart } from '@fortawesome/free-solid-svg-icons';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SliderModule } from 'primeng/slider';

import { AuthService } from '@usheru-hff/authentication/domain';
import { UserInteractionService } from '@usheru-hff/community-elements/utils';
import { getMovieGenres, getMovieTags, Movie } from '@usheru-hff/shared/domain';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { SharedUtilsTranslationModule, TranslationsService } from '@usheru-hff/shared/utils-translation';
@Component({
  selector: 'community-elements-movie-rating-handler',
  template: `
    <div class="title" i18n="Rating question@@feature-movie-rating.question">What would you rate this film?</div>
    <div class="rating-wrapper">
      <div class="hearts-container">
        <fa-icon
          (click)="setRatingValue(emptyHeartRate)"
          [icon]="emptyHeart"
          [class.active]="activeHeart === emptyHeart"
          size="2xl"
        ></fa-icon>
        <fa-icon
          (click)="setRatingValue(halfHeartRate)"
          [icon]="halfHeart"
          [class.active]="activeHeart === halfHeart"
          size="2xl"
        ></fa-icon>
        <fa-icon
          (click)="setRatingValue(fullHeartRate)"
          [icon]="fullHeart"
          [class.active]="activeHeart === fullHeart"
          size="2xl"
        ></fa-icon>
      </div>
      <p-slider [(ngModel)]="value" (onChange)="onRatingChange()"></p-slider>
      <div class="slider-details" style="left:{{ value }}%">
        <div class="slider-description__rating text">{{ value }}%</div>
        <div *ngIf="description" class="slider-description__text text">{{ description }}</div>
      </div>
    </div>

    <button
      pButton
      [loading]="waitingForResponse"
      (click)="onRateClick()"
      data-translate="public.header.link.rate"
      [label]="'public.header.link.rate' | translate"
    ></button>
  `,
  styleUrls: ['./movie-rating-handler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, ButtonModule, SliderModule, FormsModule, SharedUtilsTranslationModule]
})
export class MovieRatingHandlerComponent implements OnInit {
  private readonly userInteractionService = inject(UserInteractionService);
  private readonly messageService = inject(MessageService);
  private readonly trackingService = inject(TrackingService);
  private readonly authService = inject(AuthService);
  private readonly translationService = inject(TranslationsService);

  @Input() movie: Movie;
  @Output() complete = new EventEmitter<any>();

  readonly emptyHeartRate = 25;
  readonly halfHeartRate = 50;
  readonly fullHeartRate = 75;
  emptyHeart = faEmptyHeart;
  halfHeart = faHalfHeart;
  fullHeart = faFullHeart;

  value: number = 50;
  description: string;
  activeHeart = faHalfHeart;

  waitingForResponse = false;

  ngOnInit() {
    this.setRatingValue(this.movie?.myRank || this.value);
  }

  onRateClick() {
    this.waitingForResponse = true;
    this.userInteractionService.rateMovie(this.movie.id, this.value).subscribe({
      next: (/*res*/) => {
        this.messageService.add({
          severity: 'success',
          summary: $localize`:@@movie-rating-handler.rate.success.title:Success`,
          detail: $localize`:@@movie-rating-handler.rate.success.description:Movie rated succesfully`
        });
        this.waitingForResponse = false;
        this.movie.myRank = this.value;
        this.complete.emit(true);

        this.trackingService.trackEvent({
          action: 'RateMovie',
          properties: {
            category: 'Community',
            rating: this.value,
            movie: this.movie.title,
            idMovie: this.movie.id,
            tagsMovie: getMovieTags(this.movie),
            genresMovie: getMovieGenres(this.movie),
            user: this.username
          }
        });
      },
      error: (/*err*/) => {
        this.messageService.add({
          severity: 'error',
          summary: $localize`:@@movie-rating-handler.rate.error.title:Error`,
          detail: $localize`:@@movie-rating-handler.rate.error.description:Movie could not be rated`
        });
        this.waitingForResponse = false;
      }
    });
  }

  onRatingChange() {
    this.evaluateRating();
    this.evaluateHeart();
  }

  evaluateHeart() {
    if (this.value <= 33) {
      this.activeHeart = this.emptyHeart;
    } else if (this.value <= 66) {
      this.activeHeart = this.halfHeart;
    } else {
      this.activeHeart = this.fullHeart;
    }
  }

  setRatingValue(value: number) {
    this.value = value;
    this.onRatingChange();
  }

  evaluateRating() {
    // todo: apply rating scale provided by Luis
    if (this.value <= 20) {
      this.description = $localize`:@@movie-rating.20:Dissapointing`;
    } else if (this.value <= 40) {
      this.description = $localize`:@@movie-rating.40:Not what I was hoping for`;
    } else if (this.value <= 60) {
      this.description = $localize`:@@movie-rating.60:Mixed feelings`;
    } else if (this.value <= 75) {
      this.description = $localize`:@@movie-rating.75:Had a good time`;
    } else if (this.value <= 90) {
      this.description = $localize`:@@movie-rating.90:I really liked it`;
    } else {
      this.description = $localize`:@@movie-rating.100:I loved it`;
    }
  }

  get username() {
    return this.authService.getUserUsername();
  }
}
